'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Headphones, Play } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { PlayQueue, type PlayableItem } from '@/contexts/media';

export interface Narration extends PlayableItem {
  storyId: string;
  /** Total length in seconds, when the blocks know it. */
  seconds?: number;
}

/**
 * Listening to the library, not to one story.
 *
 * A parent with a dozen narrated stories could only open them one at a time:
 * the library row played the first narration it found, and nothing played the
 * next. So this is the one thing that was missing — a list of every narration
 * the library has, each one on its own line and pickable, and a button that
 * hands the chosen ones to the queue player (which already knew how to
 * auto-advance; nothing used it).
 *
 * Narrations, not stories: a story can be read by two voices, and "play my
 * stories" must not silently choose one of them.
 */
export function ListenQueuePanel({ items }: { items: Narration[] }) {
  const t = useTranslations('stories.listen');
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [queue, setQueue] = useState<Narration[] | null>(null);

  // Everything is on by default: the parent opened a panel called "listen",
  // not a form to fill in.
  const chosen = useMemo(() => items.filter((i) => !excluded.has(i.id)), [items, excluded]);

  const toggle = (id: string) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const minutes = (seconds?: number) =>
    seconds && seconds > 0 ? t('minutes', { minutes: Math.max(1, Math.round(seconds / 60)) }) : null;

  if (items.length === 0) {
    return (
      <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="listen-empty">
        {t('empty')}
      </p>
    );
  }

  return (
    <div className="rounded-lg border bg-card" data-testid="listen-panel">
      <div className="flex flex-wrap items-center gap-2 border-b p-3">
        <Headphones className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="text-sm font-medium">{t('title')}</span>
        <span className="text-xs text-muted-foreground">
          {t('chosen', { count: chosen.length, total: items.length })}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => setExcluded(excluded.size === 0 ? new Set(items.map((i) => i.id)) : new Set())}
            data-testid="listen-select-all"
          >
            {excluded.size === 0 ? t('clear') : t('selectAll')}
          </Button>
          <Button
            size="sm"
            className="h-8"
            disabled={chosen.length === 0}
            onClick={() => setQueue(chosen)}
            data-testid="listen-play"
          >
            <Play className="mr-1.5 size-3.5" />
            {t('play')}
          </Button>
        </div>
      </div>

      <ul className="max-h-72 divide-y overflow-y-auto">
        {items.map((item) => (
          <li key={item.id} className="flex items-center gap-3 px-3 py-2">
            <Checkbox
              id={`listen-${item.id}`}
              checked={!excluded.has(item.id)}
              onCheckedChange={() => toggle(item.id)}
              data-testid="listen-track-checkbox"
            />
            <label htmlFor={`listen-${item.id}`} className="min-w-0 flex-1 cursor-pointer">
              <span className="block truncate text-sm">{item.title}</span>
              {(item.subtitle || minutes(item.seconds)) && (
                <span className="block truncate text-xs text-muted-foreground">
                  {[item.subtitle, minutes(item.seconds)].filter(Boolean).join(' · ')}
                </span>
              )}
            </label>
          </li>
        ))}
      </ul>

      {queue && <PlayQueue items={queue} onClose={() => setQueue(null)} />}
    </div>
  );
}
